/**
 * Execution Trust Service
 * ZeroPath | ETF Foundation — Layer 1 (Declarare) + Layer 2 (Timp) + Confidence Score v1
 *
 * Apelat din taskService.completeTask() fire-and-forget.
 * Nu aruncă niciodată eroare spre caller.
 */

import { db } from '../firebase';
import { doc, updateDoc, getDoc } from 'firebase/firestore';

/**
 * Populează Layer 1 și Layer 2 după completarea unui task dintr-un workflow.
 *
 * @param {string} workflowExecutionId - ID document din workflow_executions
 * @param {object} completedTask - documentul task_instances complet (cu .id)
 * @param {string} executorName - display name din users/{uid}
 */
export async function populateExecutionTrustLayers(workflowExecutionId, completedTask, executorName) {
  try {
    const execRef = doc(db, 'workflow_executions', workflowExecutionId);
    const execSnap = await getDoc(execRef);
    if (!execSnap.exists()) return;

    const execData = execSnap.data();

    const execOrgId = execData.organization_id || execData.organizationId || null;
    const taskOrgId = completedTask.context?.organizationId || completedTask.organizationId || null;

    if (execOrgId && taskOrgId && execOrgId !== taskOrgId) {
      console.warn(`[ETF] organizationId mismatch — exec: ${execOrgId}, task: ${taskOrgId}. Aborting.`);
      return;
    }

    const currentTrust = execData.execution_trust || buildEmptyExecutionTrust(execData.pending_task_ids?.length || 0);

    // --- Layer 1: Declarare execuție ---
    const l1Entry = buildL1Entry(completedTask, executorName);
    const updatedDeclaredExecutions = [...(currentTrust.layer_1?.declared_executions || []), l1Entry];
    const updatedTaskCountCompleted = (currentTrust.layer_1?.task_count_completed || 0) + 1;

    const updatedL1 = {
      ...currentTrust.layer_1,
      declared_executions: updatedDeclaredExecutions,
      task_count_completed: updatedTaskCountCompleted,
      status: 'populated',
      populated_at: currentTrust.layer_1?.populated_at || new Date()
    };

    // --- Layer 2: Validare timp ---
    const l2Entry = buildL2Entry(completedTask);
    const updatedTimeValidations = [...(currentTrust.layer_2?.time_validations || []), l2Entry];
    const flaggedCount = updatedTimeValidations.filter(v => v.flagged).length;

    const updatedL2 = {
      ...currentTrust.layer_2,
      time_validations: updatedTimeValidations,
      flagged_count: flaggedCount,
      status: flaggedCount > 0 ? 'flagged' : 'populated',
      populated_at: currentTrust.layer_2?.populated_at || new Date()
    };

    // --- Confidence Score v1 ---
    const updatedTrust = {
      ...currentTrust,
      layer_1: updatedL1,
      layer_2: updatedL2,
      last_updated: new Date()
    };

    const cs = calculateConfidenceScore(updatedTrust);

    await updateDoc(execRef, {
      execution_trust: updatedTrust,
      confidence_score: cs
    });

    console.log(`✅ [ETF] Layers populated for execution ${workflowExecutionId} — CS: ${cs.value}`);
  } catch (err) {
    // Non-blocking — nu propagă eroarea
    console.warn('[executionTrustService] populateExecutionTrustLayers failed (non-blocking):', err?.message || err);
  }
}

/**
 * Construiește structura goală execution_trust pentru un document nou.
 * @param {number} expectedTaskCount - numărul de task-uri așteptate (din pending_task_ids.length)
 */
export function buildEmptyExecutionTrust(expectedTaskCount = 0) {
  return {
    layer_1: {
      status: 'pending',
      declared_executions: [],
      populated_at: null,
      task_count_expected: expectedTaskCount,
      task_count_completed: 0
    },
    layer_2: {
      status: 'pending',
      time_validations: [],
      flagged_count: 0,
      populated_at: null
    },
    layer_3: null,
    layer_4: null,
    layer_5: null,
    layer_6: null,
    layer_7: null,
    layer_8: null,
    active_layers: ['layer_1', 'layer_2'],
    last_updated: null
  };
}

// ============================================================
// Funcții interne
// ============================================================

function buildL1Entry(task, executorName) {
  const protocol = task.protocols?.[0] || {};
  return {
    task_id: task.id,
    task_name: task.name || '',
    protocol_id: protocol.protocolId || '',
    protocol_name: protocol.protocolName || '',
    executor_id: task.assignedTo || task.updatedBy || '',
    executor_name: executorName || '',
    declared_at: task.actualEnd || null,
    compliance_score: task.complianceScore ?? null,
    location: {
      department_id: task.context?.departmentId || null,
      room_id: task.context?.roomId || null
    }
  };
}

function buildL2Entry(task) {
  const scheduledStart = task.scheduledStart?.toMillis?.() || null;
  const scheduledEnd = task.scheduledEnd?.toMillis?.() || null;
  const actualStart = task.actualStart?.toMillis?.() || null;
  const actualEnd = task.actualEnd?.toMillis?.() || null;

  const estimatedMinutes = scheduledStart !== null && scheduledEnd !== null
    ? Math.round((scheduledEnd - scheduledStart) / 60000)
    : null;

  const actualMinutes = actualStart !== null && actualEnd !== null
    ? Math.round((actualEnd - actualStart) / 60000)
    : null;

  const ratio = estimatedMinutes !== null && actualMinutes !== null && estimatedMinutes > 0
    ? Math.round((actualMinutes / estimatedMinutes) * 100) / 100
    : null;

  const flagged = ratio !== null && ratio < 0.30;

  return {
    task_id: task.id,
    protocol_id: task.protocols?.[0]?.protocolId || '',
    scheduled_start: task.scheduledStart || null,
    actual_start: task.actualStart || null,
    scheduled_end: task.scheduledEnd || null,
    actual_end: task.actualEnd || null,
    estimated_duration_minutes: estimatedMinutes,
    actual_duration_minutes: actualMinutes,
    duration_ratio: ratio,
    flagged,
    flag_reason: flagged ? 'sub_30_percent_estimated_duration' : null
  };
}

function calculateL1Score(layer_1) {
  const executions = layer_1?.declared_executions || [];
  if (executions.length === 0) return 0;
  const scores = executions
    .map(e => e.compliance_score)
    .filter(s => s !== null && s !== undefined);
  if (scores.length === 0) return 0;
  return Math.round(scores.reduce((sum, s) => sum + s, 0) / scores.length);
}

function calculateL2Score(layer_2) {
  const validations = layer_2?.time_validations || [];
  if (validations.length === 0) return 100;
  const flagged = layer_2?.flagged_count || 0;
  return Math.round(((validations.length - flagged) / validations.length) * 100);
}

function calculateConfidenceScore(execution_trust) {
  const l1Score = calculateL1Score(execution_trust.layer_1);
  const l2Score = calculateL2Score(execution_trust.layer_2);

  const L1_WEIGHT = 0.6;
  const L2_WEIGHT = 0.4;
  const value = Math.round(l1Score * L1_WEIGHT + l2Score * L2_WEIGHT);

  const flags = [];
  if ((execution_trust.layer_2?.flagged_count || 0) > 0) {
    flags.push('l2_time_anomaly');
  }

  return {
    value,
    version: 'v1',
    calculated_at: new Date(), // Nu folosim serverTimestamp() — nu merge în obiect nested
    components: {
      layer_1_score: l1Score,
      layer_2_score: l2Score,
      layer_1_weight: L1_WEIGHT,
      layer_2_weight: L2_WEIGHT
    },
    flags
  };
}

/**
 * Populează layer_learning după execuția unui nod integration/learning_check.
 * Non-blocking — nu aruncă erori spre caller.
 *
 * @param {string} workflowExecutionId
 * @param {Array} taskResults - array de { task_id, staff_id, protocol_id, user_group_id, passed, score, completed_at, reason }
 */
export async function populateLearningCheckResult(workflowExecutionId, taskResults) {
  try {
    const execRef = doc(db, 'workflow_executions', workflowExecutionId);

    const passedCount = taskResults.filter(r => r.passed).length;

    const layerLearning = {
      status: taskResults.length === 0 ? 'not_applicable' : 'populated',
      checks: taskResults,
      passed_count: passedCount,
      total_count: taskResults.length,
      checked_at: new Date()
    };

    await updateDoc(execRef, {
      'execution_trust.layer_learning': layerLearning,
      'execution_trust.last_updated': new Date()
    });

    console.log(`✅ [ETF] layer_learning populated for execution ${workflowExecutionId} — ${passedCount}/${taskResults.length} passed`);
  } catch (err) {
    console.warn('[executionTrustService] populateLearningCheckResult failed (non-blocking):', err?.message || err);
  }
}

/**
 * Populează layer_5 (ATP) după înregistrarea rezultatelor ATP din workflow (scheduler viitor).
 * Non-blocking — nu aruncă erori spre caller.
 *
 * @param {string} workflowExecutionId
 * @param {Array} atpChecks - { atp_record_id, atp_id, room_id, surface_type, value, interpretation, recorded_at, operator_id }
 */
export async function populateATPResult(workflowExecutionId, atpChecks) {
  try {
    const execRef = doc(db, 'workflow_executions', workflowExecutionId);

    const conformCount = atpChecks.filter(c => c.interpretation === 'conform').length;
    const neconformCount = atpChecks.filter(c => c.interpretation === 'neconform').length;

    const layer5 = {
      status: atpChecks.length === 0 ? 'not_applicable' : 'populated',
      atp_checks: atpChecks,
      conform_count: conformCount,
      neconform_count: neconformCount,
      total_count: atpChecks.length,
      checked_at: new Date()
    };

    await updateDoc(execRef, {
      'execution_trust.layer_5': layer5,
      'execution_trust.last_updated': new Date()
    });

    console.log(`✅ [ETF] layer_5 populated — ${conformCount} conform, ${neconformCount} neconform`);
  } catch (err) {
    console.warn('[executionTrustService] populateATPResult failed (non-blocking):', err?.message || err);
  }
}
